import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import classNames from 'classnames';
import CodeEditor from '../CodeEditor/CodeEditor';
import MarkdownPreview from '../MarkdownPreview/MarkdownPreview';
import { getInputSizeStyles } from '../utils';
/**
 * @export
 * A markdown editor with a Write / Preview tab switcher.
 * Write tab uses CodeEditor with markdown syntax highlighting.
 * Preview tab renders the markdown using MarkdownPreview with UXP prose styles.
 *
 * Both panes stay mounted so the editor preserves scroll position on tab switch.
 *
 * @example
 * ```tsx
 * <MarkdownEditor value={md} onChange={setMd} fullWidth height="400px" />
 * ```
 */
const MarkdownEditor = forwardRef((props, ref) => {
    const { value, onChange, className, placeholder, lineNumbers = false, indentUnit, height, minHeight, maxHeight, defaultTab = 'write', activeTab, hideTabs = false, previewEmptyText, hideToolbar, compactToolbar, readOnly, disabled, isValid, minWidth, maxWidth, width, fullWidth, } = props;
    const [internalTab, setInternalTab] = useState(defaultTab);
    const codeEditorRef = useRef(null);
    const isControlled = activeTab !== undefined;
    const currentTab = isControlled ? activeTab : internalTab;
    useImperativeHandle(ref, () => ({
        focus: () => { var _a; return (_a = codeEditorRef.current) === null || _a === void 0 ? void 0 : _a.focus(); },
        setTab: (tab) => setInternalTab(tab),
        toggleTab: () => setInternalTab((t) => (t === 'write' ? 'preview' : 'write')),
        getTab: () => currentTab,
    }), []);
    const sizeStyles = getInputSizeStyles({ minWidth, maxWidth, width, fullWidth, height, minHeight, maxHeight });
    return (_jsxs("div", { className: classNames('uxpcore_markdown_editor', className, {
            'uxpcore_markdown_editor--full-width': fullWidth,
            'uxpcore_markdown_editor--disabled': disabled,
            'uxpcore_markdown_editor--readonly': readOnly,
        }), style: sizeStyles, children: [!isControlled && !hideTabs && (_jsxs("div", { className: "uxpcore_markdown_editor__tabs", children: [_jsx("button", { className: classNames('uxpcore_markdown_editor__tab', { '--active': currentTab === 'write' }), onClick: () => setInternalTab('write'), type: "button", children: "Write" }), _jsx("button", { className: classNames('uxpcore_markdown_editor__tab', { '--active': currentTab === 'preview' }), onClick: () => setInternalTab('preview'), type: "button", children: "Preview" })] })), _jsx("div", { className: "uxpcore_markdown_editor__pane", hidden: currentTab !== 'write', children: _jsx(CodeEditor, { ref: codeEditorRef, value: value, onChange: onChange, language: "markdown", lineNumbers: lineNumbers, indentUnit: indentUnit, height: "100%", fullWidth: true, placeholder: placeholder, readOnly: readOnly, disabled: disabled, isValid: isValid, hideToolbar: hideToolbar, compactToolbar: compactToolbar }) }), _jsx("div", { className: "uxpcore_markdown_editor__pane uxpcore_markdown_editor__pane--preview", hidden: currentTab !== 'preview', children: _jsx(MarkdownPreview, { value: value, emptyText: previewEmptyText }) })] }));
});
MarkdownEditor.displayName = 'MarkdownEditor';
export default MarkdownEditor;
